<template>
  <div>
    <v-card class="pa-5">
      <v-card-text>
        <v-card-title>
            <h4>{{$t('reports.inspectionUnitTypes')}}</h4>
            <v-spacer></v-spacer>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn @click="filterData()" v-on="on" v-bind="attrs" depressed class="float-right mr-2" color="primary">
                  <v-icon small>fas fa-search</v-icon>
                </v-btn>
              </template>
              <span>{{$t('general.search')}}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn @click="resetFilter" v-on="on" v-bind="attrs" depressed class="float-right mr-2" color="primary">
                  <v-icon small>fa-solid fa-eraser</v-icon>
                </v-btn>
              </template>
              <span>{{$t('general.resetFilter')}}</span>
            </v-tooltip>
            <v-btn v-if="report.length > 0" :loading="loadingButton" class="mr-2" @click="download" color="primary">
              {{$t('reports.downloadReport')}}
              <v-icon small class="ml-2"> fas fa-cloud-download-alt </v-icon>
            </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form v-model="valid" lazy-validation ref="form">
            <v-row dense>
              <v-col cols="3">
                <v-autocomplete
                  clearable
                  outlined
                  dense
                  :items="sites"
                  item-text="name"
                  item-value="id"
                  v-model="filter.site"
                  :label="$t('general.site')"
                />
              </v-col>
              <v-col cols="3">
                <v-autocomplete
                  clearable
                  outlined
                  dense
                  :items="unitTypes"
                  item-text="alias"
                  item-value="id"
                  v-model="filter.unit_type"
                  :label="$t('general.unitType')"
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  outlined
                  dense
                  :rules="rules.textNoSpecialCharsFilters"
                  :label="$t('general.plates')"
                  v-model="filter.plate"
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  outlined
                  dense
                  :rules="rules.textNoSpecialChars"
                  :label="$t('general.unitNumber')"
                  v-model="filter.unit_number"
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  outlined
                  dense
                  label="VIN"
                  :rules="rules.textNoSpecialChars"
                  v-model="filter.vin_number"
                />
              </v-col>
              <v-col v-if="filter.dateRange" cols="3">
                <v-menu
                  v-model="menu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filter.start_date"
                      :label="$t('general.startDate')"
                      outlined
                      dense
                      prepend-icon="event"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    no-title
                    color="primary"
                    v-model="filter.start_date"
                    @input="handleStartDateSelection"
                    :allowed-dates="allowedStartDates"
                    :day-style="getDayStyle"
                  />
                </v-menu>
              </v-col>
              <v-col v-if="filter.dateRange" cols="3">
                <v-menu
                  v-model="menu2"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filter.end_date"
                      :label="$t('general.endDate')"
                      outlined
                      dense
                      prepend-icon="event"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    no-title
                    color="primary"
                    v-model="filter.end_date"
                    @input="menu2 = false"
                    :allowed-dates="allowedEndDates"
                    :day-style="getDayStyle"
                  />
                </v-menu>
              </v-col>
              <v-col cols="3">
                <v-checkbox v-model="filter.dateRange" :label="$t('general.dateRange')" class="mt-0" hide-details color="primary"/>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-overlay absolute opacity="0" :value="loadingTable"/>
        <v-data-table
          :loading="loadingTable"
          :loading-text="$t('general.loading')"
          :headers="headers"
          :items="report"
          :server-items-length="optionsDataTable.totalItems"
          :options.sync="optionsDataTable"
          :footer-props="{ 'items-per-page-options': [50, 70, 100] }"
          :single-expand="false"
          @update:page="get"
          @update:items-per-page="get"
          @update:sort-by="get"
          @update:sort-desc="get"
          item-key="id"
          show-expand
        >
          <template v-slot:item.site="{ item }">
            {{item.site && item.site.name ? item.site.name : '--'}}
          </template>
          <template v-slot:item.user="{ item }">
            {{item.user && item.user.name ? item.user.name : '--'}}
          </template>
          <template v-slot:item.vin_number="{ item }">

            <span>
              {{ getVinNumber(item) }} 
              <v-chip v-if="getPlates(item) > 1" small>
                +{{ getPlates(item) }}
              </v-chip>
            </span>
            <!-- 
            <span v-if="item.truck !== null">
              {{item.truck.plates.length > 0 ? item.truck.plates[0].plate_number : '- -'}}&nbsp;
              <v-chip v-if="item.truck.plates.length > 1" small>
                +{{ item.truck.plates.length - 1 }}
              </v-chip>
            </span>
            <span v-if="item.truck === null && item.container !== null">
              {{item.container.plates.length > 0 ? item.container.plates[0].plate_number : '- -'}}&nbsp;
              <v-chip v-if="item.container.plates.length > 1" small>
                +{{ item.container.plates.length - 1 }}
              </v-chip>
            </span> -->
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="py-3">
              <h3 class="pb-1">{{item.name}}</h3>
              <div v-if="item.sections.length > 0" class="sections-container">
                <div v-if="item.sections.length > 0" class="sections-container">
                  <div v-for="section in item.sections" :key="section.id" class="section-box">
                    <div>
                      <strong>{{ $t('general.vin') }}:</strong>
                      {{ section.unit ? section.unit.vin_number : '' }}
                    </div>
                    <div>
                      <strong>{{ $t('general.unitType') }}:</strong>
                      {{ (section.unit_types && section.unit_types.length > 0) ? section.unit_types[0].alias : '' }}
                    </div>
                    <div>
                      <strong>{{ $t('monitoring.numberEconomic') }}:</strong>
                      {{ section.unit ? section.unit.unit_number : '' }}
                    </div>
                    <div>
                      <strong>{{ $t('general.plates') }}:</strong>
                      <span v-if="section.unit && section.unit.plates.length > 0">
                        <span v-for="plate in section.unit.plates" :key="plate.id">
                          {{ plate.plate_number }}
                        </span>
                      </span>
                      <span v-else>{{ $t('units.noPlates') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon class="mr-4" small @click="viewItem(item.id)" v-on="on">far fa-eye</v-icon>
              </template>
              <span>{{$t('general.view')}}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
  export default {
    data () {
      return {
        filterModal: false,
        valid: true,
        loadingTable: false,
        loadingButton: false,
        firstLoad: true,
        menu: false,
        menu2: false,
        link: '',
        options: {
          itemsPerPage: 15,
          page: 1,
          total: 0
        },
        optionsDataTable: {
          itemsPerPage: 15,
          page: 1,
          total: 0
        },
        filter: {
          site: null,
          unit_type: null,
          plate: '',
          vin_number: '',
          unit_number: '',
          dateRange: true,
          start_date: this.returnFirstDayMount(),
          end_date: this.returnUltimateDayMount()
        },
        report: [],
        sites: [],
        unitTypes: []
      }
    },
    watch: {
      sites (val) {
        if (val.length > 0) this.options.itemsPerPage = 10
      },
      'filter.dateRange' (val) {
        if (!val) {
          this.filter.start_date = null
          this.filter.end_date = null
        } else {
          this.filter.start_date = this.returnFirstDayMount()
          this.filter.end_date = this.returnUltimateDayMount()
        }
      }
    },
    computed: {
      headers() {
        return [
          {
            text: this.$t('general.noInspection'),
            value: 'consecutive_number',
            align: 'center'
          },
          {
            text: this.$t('general.date'),
            align: 'center',
            value: 'created_at'
          },
          {
            text: this.$t('general.name'),
            align: 'center',
            value: 'name'
          },
          {
            text: 'Inspector',
            align: 'center',
            value: 'user'
          },
          {
            text: this.$t('general.site'),
            align: 'center',
            value: 'site'
          },
          {
            text: this.$t('general.duration'),
            align: 'center',
            value: 'duration'
          },
          {
            text: this.$t('general.vin'),
            align: 'center',
            value: 'vin_number'
          },
          {
            text: this.$t('general.actions'),
            align: 'center',
            value: 'actions',
            sortable: false
          }
        ]
      },
      rules () {
        return this.$validationRules()
      }
    },
    created () {
      this.$store.dispatch('sites/get', { event: { context: this, item: { perPage: -1, page: 1 } } })
      this.$store.dispatch('unitTypes/getAll', { event: { context: this } })
      this.get()
    },
    methods:{
      returnFirstDayMount() {
        const now = new Date()
        return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().substr(0, 10)
      },
      returnUltimateDayMount() {
        const now = new Date()
        return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().substr(0, 10)
      },
      resetFilter() {
        this.filter = {
          site: null,
          unit_type: null,
          plate: '',
          vin_number: '',
          unit_number: '',
          dateRange: true,
          start_date: this.returnFirstDayMount(),
          end_date: this.returnUltimateDayMount()
        }

        if(this.optionsDataTable.page === 1) return this.get()

        this.optionsDataTable.page = 1        
      },
      filterData(){
        if(!this.$refs.form.validate()) return this.showNotification(this.$t('general.errorFilterMessage'), 'warn', -1)

        if(this.optionsDataTable.page === 1) return this.get()

        this.optionsDataTable.page = 1
      },
      viewItem (id) {
        this.$router.push('/inspectionUnitTypes/view/' + id)
      },
      download () {
        if(!this.$refs.form.validate()) return this.showNotification(this.$t('general.errorFilterMessage'), 'warn', -1)

        this.loadingButton = true

        this.$store.dispatch('reports/getInspectionsFilterDoc', {
          event: {
            context: this,
            params: {
              perPage: this.options.itemsPerPage,
              page: this.options.page,
              site_id: this.filter.site,
              unit_type_id: this.filter.unit_type,
              unit_number: this.filter.unit_number,
              vin_number: this.filter.vin_number,
              plates: this.filter.plate,
              start_date: this.filter.start_date,
              end_date: this.filter.end_date,
              type: 'download',
              sortKey: this.options.sortBy ? this.options.sortBy[0] : 'id',
              ord: this.options.sortDesc && this.options.sortDesc[0] ? 'asc' : 'desc'
            }
          }
        })
      },
      get () { 
        this.loadingTable = true
        this.$store.dispatch('reports/getInspectionsFilter', {
          event: {
            context: this,
            params: {
              end_date: this.filter.end_date === null ? undefined : this.filter.end_date,
              start_date: this.filter.start_date === null ? undefined : this.filter.start_date,
              site_id: this.filter.site,
              plate: this.filter.plate === '' ? undefined : this.filter.plate ,
              unit_type_id: this.filter.unit_type === '' ? undefined : this.filter.unit_type,
              unit_number: this.filter.unit_number === '' ? undefined : this.filter.unit_number,
              vin_number: this.filter.vin_number === '' ? undefined : this.filter.vin_number,
              page: this.optionsDataTable.page,
              perPage: this.optionsDataTable.itemsPerPage,
              type: 'json',
              sortKey: (this.optionsDataTable.sortBy && this.optionsDataTable.sortBy.length > 0) ? this.optionsDataTable.sortBy[0] : 'id',
              ord: this.optionsDataTable.sortDesc && this.optionsDataTable.sortDesc[0] ? 'asc' : 'desc'
            }
          }
        })
      },
      handleStartDateSelection(date) {
        this.filter.start_date = date
        this.updateEndDate()
        this.menu = false
      },
      allowedStartDates() {
        return true
      },
      allowedEndDates(date) {
        if (!this.filter.start_date) return true

        const startDate = new Date(this.filter.start_date)
        const endDateLimit = new Date(startDate)
        endDateLimit.setDate(startDate.getDate() + 29)

        return new Date(date) >= startDate && new Date(date) <= endDateLimit
      },
      updateEndDate() {
        if (this.filter.start_date) {
          const startDate = new Date(this.filter.start_date)
          const endDateLimit = new Date(startDate)
          endDateLimit.setDate(startDate.getDate() + 29)

          this.filter.end_date = endDateLimit.toISOString().substr(0, 10)
        }
      },
      getDayStyle(date) {
        if ((this.filter.start_date && this.allowedEndDates(date)) || (!this.filter.start_date && this.allowedStartDates(date))) {
          return null
        }
        return { backgroundColor: 'rgba(0, 0, 0, 0.1)', color: 'gray' }
      },
      getVinNumber(item) {
        if(!item.sections || item.sections.length === 0) return '--'
       
        const response = item.sections.find((section) => {
          return section.unit && section.unit.vin_number
        })

        return response && response.unit ? response.unit.vin_number : '--'
        //  item.sections.length > 0 && item.sections[0].unit ? item.sections[0].unit.vin_number : '--'  //LÓGICA ORIGINAL
      },
      getPlates(item) {
        if(!item.sections || item.sections.length === 0) return 0

        const response = item.sections.find((section) => {
          return section.unit && section.unit.plates && section.unit.plates.length > 0
        })

        return response && response.unit ? response.unit.plates.length - 1 : 0
        // CÓDIGO ORIGINAL
        // item.sections[0].unit && item.sections[0].unit.plates.length
        //  item.sections[0].unit.plates.length  - 1 
      }
    }
  }
</script>

<style scoped>
.sections-container{
  display:flex;
  flex-wrap:wrap;
  gap:20px;
}

.section-box{
  width:300px;
  border-right:1px solid #ddd;
  padding-right:20px;
}
</style>